/**
 * Hooks for vehicle lookup and address autocomplete.
 *
 * Vehicle lookup: POST /api/lookup/vehicle/
 * Address autocomplete: GET /api/lookup/address/autocomplete/?term=
 * Address resolve: GET /api/lookup/address/resolve/?id=&term=
 */

import { useMutation, useQuery, type QueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type {
  AddressLookupAPI,
  AddressLookupCapabilities,
} from '@/shared/components/ui/address-lookup-context'
import type { PostcodeLookupResult } from '@/shared/lib/postcode-lookup'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VehicleLookupResult {
  registration: string
  make: string
  model: string
  colour: string
  year: string
  fuel_type: string
  engine_type: string
  tax_status: string
  mot_status: string
  month_of_first_registration: string
  // Provider-dependent: DVLA returns neither, the commercial providers do.
  variant?: string
  vin?: string
}

export interface AddressSuggestion {
  id: string
  address: string
  /** ``> 1`` marks a group that must be expanded before it can be resolved. */
  entries?: number
}

export interface ResolvedAddress {
  line_1: string
  line_2: string
  town_or_city: string
  county: string
  postcode: string
  country: string
}

// ---------------------------------------------------------------------------
// Vehicle lookup
// ---------------------------------------------------------------------------

export function useVehicleLookup() {
  return useMutation({
    mutationFn: async (registrationNumber: string) => {
      const { data } = await api.post<VehicleLookupResult>(
        '/lookup/vehicle/',
        { registration_number: registrationNumber },
      )
      return data
    },
  })
}

// ---------------------------------------------------------------------------
// Address autocomplete
// ---------------------------------------------------------------------------

export function useAddressAutocomplete(term: string) {
  return useQuery({
    queryKey: ['address-autocomplete', term],
    queryFn: async () => {
      const params: Record<string, string> = { term }
      const { data } = await api.get<{ suggestions: AddressSuggestion[] }>(
        '/lookup/address/autocomplete/',
        { params },
      )
      return data.suggestions
    },
    enabled: term.length >= 3,
    staleTime: 1000 * 60 * 60, // 1 hour - matches backend cache
  })
}

export function useAddressResolve() {
  return useMutation({
    mutationFn: async ({ id, term }: { id: string; term: string }) => {
      const params: Record<string, string> = { id, term }
      const qs = new URLSearchParams(params).toString()
      const { data } = await api.get<ResolvedAddress>(
        `/lookup/address/resolve/?${qs}`,
      )
      return data
    },
  })
}

/**
 * Adapt the admin address-lookup endpoints into the shared `AddressLookupAPI`
 * consumed by `AddressInput` (via `AddressLookupProvider`). Routed through the
 * React Query cache so autocomplete/resolve share the same `address-autocomplete`
 * / `address-resolve` keys (SEARCH policy) as `useAddressAutocomplete` instead
 * of re-hitting the server. Shared by the entity editor and the multi-item
 * claim intake so the two never drift.
 */
export function createAddressLookupApi(queryClient: QueryClient): AddressLookupAPI {
  return {
    // ``country`` is part of the cache key AND the request: a term cached for one
    // country must never be served for another (the backend cache is likewise
    // country-scoped).
    autocomplete: (term, country) =>
      queryClient.fetchQuery({
        queryKey: ['address-autocomplete', country ?? '', term],
        queryFn: async () => {
          const params: Record<string, string> = { term }
          if (country) params.country = country
          const { data } = await api.get<{
            suggestions: AddressSuggestion[]
          }>('/lookup/address/autocomplete/', { params })
          return data.suggestions
        },
      }),
    expand: (id) =>
      queryClient.fetchQuery({
        queryKey: ['address-expand', id],
        queryFn: async () => {
          const params: Record<string, string> = { id }
          const { data } = await api.get<{
            suggestions: AddressSuggestion[]
          }>('/lookup/address/expand/', { params })
          return data.suggestions
        },
      }),
    capabilities: () =>
      queryClient.fetchQuery({
        queryKey: ['address-capabilities'],
        staleTime: 60 * 60 * 1000,
        queryFn: async () => {
          const { data } = await api.get<{
            enabled: boolean
            default_country: string
            countries: string[]
          }>('/lookup/address/capabilities/')
          return {
            enabled: data.enabled,
            defaultCountry: data.default_country ?? '',
            countries: data.countries ?? [],
          } satisfies AddressLookupCapabilities
        },
      }),
    resolve: (id, term) =>
      queryClient.fetchQuery({
        queryKey: ['address-resolve', id, term],
        queryFn: async () => {
          const params: Record<string, string> = { id, term }
          const qs = new URLSearchParams(params).toString()
          const { data } = await api.get<ResolvedAddress>(
            `/lookup/address/resolve/?${qs}`,
          )
          return {
            addressLine1: data.line_1 ?? '',
            addressLine2: data.line_2 ?? '',
            locality: data.town_or_city ?? '',
            region: data.county ?? '',
            postalCode: data.postcode ?? '',
            countryCode: data.country ?? '',
          }
        },
      }),
    // Postal-code -> valid cities, from the platform reference table (server
    // caches the result). Routed through React Query so repeated on-blur
    // lookups of the same ZIP share one cache entry (REFERENCE-style data).
    lookupPostcode: (postcode, country) =>
      queryClient.fetchQuery({
        queryKey: ['address-postcode', country, postcode],
        staleTime: 60 * 60 * 1000,
        queryFn: async () => {
          const params: Record<string, string> = { postcode }
          if (country) params.country = country
          const { data } = await api.get<{ options: PostcodeLookupResult[] }>(
            '/lookup/address/postcode/',
            { params },
          )
          return data.options ?? []
        },
      }),
  }
}
